/**
 * The ui's timing and its few drawing numbers (presentation, not balance).
 * How long the DOM overlays last, ms (`uiTiming.labels`, `uiTiming.banner`):
 * a floating label rises and fades over its kind's duration, a banner holds
 * for its own. After the dungeon-floors prototype's `dungeon-events.js`
 * (labels 900, level and learned 1200, the bag-full and charm labels 1100,
 * banners 900 at normal speed; banners a little longer here, to be read).
 * Presentation, not balance.
 */
export const uiTiming = {
  labels: { damage: 900, heal: 900, miss: 900, exp: 900, level: 1200, skill: 1000, status: 1000, stage: 1000, lost: 1400, note: 900 },
  banner: 1600,
  /** The least time a banner holds before the next one queued replaces it. */
  bannerMinHold: 800,
  /** The least time between two labels of one unit (exp, a level, a skill learned come together): the next waits, so the earlier has risen clear. */
  labelGapMs: 320,
  /**
   * Touch input (integration plan slice 4). A held pad button repeats its move
   * every `padRepeatMs` once the last one has played (the dungeon-floors
   * prototype's `dungeon-main.js`: a held direction steps again 150 ms after the
   * last step, when the leader is not moving). A pointer down and up within
   * `tapMaxMs` and `tapMaxPx` is a tap; a drag of at least `swipeMinPx` is a
   * swipe (a turn); anything between is ignored.
   */
  padRepeatMs: 150,
  tapMaxMs: 350,
  tapMaxPx: 14,
  swipeMinPx: 40,
  /** The event log's lines while collapsed: portrait and landscape. */
  logLinesPortrait: 2,
  logLinesLandscape: 3,
  /** How long the viewport must keep one size before the canvas takes it (the address bar's show and hide, a rotation). */
  resizeSettleMs: 180,
  /**
   * The minimap's drawing (lifted from the prototype's `dungeon-minimap.js`):
   * the most device pixels per CSS pixel it draws at, its inner margin (CSS
   * pixels), and the leader dot's radius and outline (CSS pixels).
   */
  minimap: { maxPixelRatio: 2, margin: 4, dotRadius: 3, dotOutline: 1.5 },
  /**
   * A walkable scene (slice 6a, lantern-water's `main.js`): a portal's fade to
   * the veil and the hold before it lifts (470 and 140 ms), and the longest
   * step the walking model takes in one frame (50 ms: a background tab never
   * teleports the traveler).
   */
  sceneFadeMs: 470,
  sceneFadeHoldMs: 140,
  sceneMaxStepMs: 50,
  /**
   * The retro kit (slice 6b): how long the location's name shows on arrival
   * before it fades, the blink period of the dialogue's ▼ (text itself
   * appears at once: no typewriter), how long the town waits once a run's
   * end has shown (its last events played and its end banner on screen: the
   * banner's 1600 ms and a beat), and the fade of a panel that leaves.
   */
  locationMs: 2500,
  dialoguePageMs: 900,
  returnMs: 2600,
  panelFadeMs: 400,
  /**
   * The airship under way (ADR 0033): how long the deck flies with the banner "Flying to {place}" before the run
   * starts on the same canvas (a banner's time and a beat: the sails set, the jetty falls astern).
   */
  flightMs: 2400,
  /**
   * The skip's confirmation (slice 8h fix round, ADR 0042): how often, while it is open, its time left and each row's
   * waste are worked out again from the server's clock (and the account read again once the whole minutes left have
   * changed, so the game's row names the plan the server would pick now).
   */
  skipTickMs: 15_000,
} as const;

export type LabelKind = keyof typeof uiTiming.labels;
